import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, CircleDashed, CreditCard, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { cadStr } from "@/lib/pricing";

// The four Starter/Pro prices in the Stripe account this server uses (test in preview, live in production).
export function StripePricesCard() {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => {
    api.get("/admin/pricing/stripe-prices").then((r) => { setD(r.data); setErr(null); })
      .catch((e) => setErr(e?.response?.data?.detail || "Could not reach Stripe."));
  }, []);
  useEffect(() => { load(); }, [load]);

  const setup = async () => {
    try {
      setBusy(true);
      const { data } = await api.post("/admin/pricing/stripe-prices/setup");
      setD(data);
      toast.success(data.created.length ? `Created ${data.created.length} Stripe price(s)` : "All Stripe prices were already set up");
    } catch (e) {
      toast.error(e?.response?.status === 403 ? "Super admin only" : e?.response?.data?.detail || "Stripe setup failed");
    } finally {
      setBusy(false);
    }
  };

  const ready = d ? d.prices.filter((p) => p.ready).length : 0;
  return (
    <div className="pf-card p-5 mt-6" data-testid="admin-stripe-prices">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="inline-flex items-center gap-2 font-semibold text-slate-900">
          <CreditCard className="w-4 h-4" /> Stripe subscription prices
          {d && (
            <span data-testid="admin-stripe-prices-mode"
                  className={`text-[10px] uppercase tracking-widest rounded-full px-2 py-0.5 ${d.mode === "live" ? "bg-emerald-600 text-white" : "bg-amber-100 text-amber-800"}`}>
              {d.mode === "live" ? "Live mode" : "Test mode"}
            </span>
          )}
        </div>
        <button onClick={setup} disabled={busy || !d || d.all_ready} data-testid="admin-stripe-prices-setup"
                className="pf-btn-primary px-4 py-2 rounded-full text-xs font-semibold inline-flex items-center gap-2 disabled:opacity-50">
          {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />}{d?.all_ready ? "All set" : "Create Stripe prices"}
        </button>
      </div>
      {err && <div className="mt-3 text-xs text-red-700" data-testid="admin-stripe-prices-error">{err}</div>}
      {d && (
        <>
          <div className="mt-2 text-xs text-slate-600" data-testid="admin-stripe-prices-summary">
            {ready} of {d.prices.length} ready. Creating prices never changes what existing subscribers pay.
          </div>
          <div className="mt-3 overflow-x-auto">
            <table className="w-full text-sm">
              <tbody>
                {d.prices.map((p) => (
                  <tr key={p.lookup_key} className="border-t border-white/60" data-testid={`admin-stripe-price-${p.plan}-${p.cadence}`}>
                    <td className="py-2 pr-4 font-medium">{p.plan_label} · {p.cadence}</td>
                    <td className="py-2 pr-4 font-mono text-xs">{cadStr(p.expected_cad)}/{p.interval}</td>
                    <td className="py-2 pr-4 font-mono text-[11px] text-slate-500 break-all">{p.price_id || "Not created"}</td>
                    <td className="py-2 text-xs">
                      {p.ready
                        ? <span className="inline-flex items-center gap-1 text-emerald-700"><CheckCircle2 className="w-3.5 h-3.5" /> Ready</span>
                        : <span className="inline-flex items-center gap-1 text-amber-700"><CircleDashed className="w-3.5 h-3.5" /> Missing</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
